/** Таблица вкладки «ФБС» — тот же грид, что и на «Распределении».
 *
 *  Общее с распределением переиспользуется, а не копируется: заголовки с сортировкой и фильтрами
 *  (`HeaderCell`, `FilterChips` из `table.tsx`), ширины колонок с ручкой ресайза
 *  (`useColumnResize`), ячейка грида (`Cell` из `shared.tsx`). Строка одноэтажная (высота
 *  `ROW_H`), название товара — в одну строку с многоточием и полным именем в подсказке.
 *  Виртуализации здесь нет намеренно: строк по складу ФБС — сотни, а не тысячи.
 *
 *  Подсветка — `fbsHighlight.ts` (пороги из `report.params`), легенда над таблицей красит теми же
 *  классами. Правки «К перемещению» живут в `fbsEdit.ts` у страницы: таблица их только показывает.
 *
 *  ⚠️ Сортировка и фильтр колонки «К перемещению» — по введённому числу (с правкой), но ПРИМЕНЁННОМУ:
 *  пока курсор в поле, порядок и фильтр держат правки на момент входа в поле (`frozen`), иначе строка
 *  уезжала бы из-под курсора на каждой набранной цифре; ушёл из поля — пересчёт. Кнопки «Обнулить» /
 *  «Вернуть расчёт» и ↺ применяются сразу (фокус уходит на кнопку). 16.10.2026: раньше сортировка
 *  шла по расчётному `recommended`, и после «Обнулить» нули с большим расчётом стояли наверху.
 */
import type { ColumnDef, FilterFn } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AlertTriangle, HelpCircle } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";

import type * as api from "@/api/supply";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { dec1, fmt, int0 } from "./format";
import {
  CUT_CLASS,
  DANGER_CLASS,
  depthClass,
  qtyClass,
  stockClass,
} from "./fbsHighlight";
import type { FbsCardCtx } from "./fbsCards";
import { StockCell, TransitCell } from "./FbsInfoCards";
import { NEGATIVE_STOCK_HINT, stockBreakdown } from "./fbsStock";
import { initialQty } from "./fbsEdit";
import type { FbsEdits } from "./fbsEdit";
import { Cell } from "./shared";
import { FilterChips, HeaderCell, metaOf, passesFilter } from "./table";
import type { ColMeta } from "./table";
import { useColumnResize } from "./useColumnResize";

const ROW_H = 28; // плотная строка: 12px кегль + инпут h-6 — как на «Распределении»
const FRS_STORAGE = "fbsGridFrsV2"; // V2: «Товар» получил основную ширину, числа ужаты

/** Ширины по id колонки (fr), как на «Распределении»: по id, а не по позиции. Колонок здесь
 * меньше, чем там, поэтому «Товар» забирает бóльшую долю — иначе названия режутся на половине. */
const DEFAULT_FRS: Record<string, number> = {
  article: 0.9,
  name: 4.8,
  orders: 0.65,
  speed: 0.75,
  stock: 0.8,
  transit: 0.65,
  depth: 0.8,
  need: 1.0,
  source_free: 0.85,
  qty: 1.1,
  marks: 0.6,
};

/** Человекочитаемые ярлыки флагов строки (коды приходят с бэка, `fbs_calc.FLAG_*`). */
const FLAG_LABEL: Record<string, string> = {
  no_sales: "нет продаж",
  no_in_box: "нет кратности короба",
  source_limited: "срезано остатком ГП",
  not_in_meta: "нет SKU WB в 1С",
};
const FLAG_HINT: Record<string, string> = {
  no_sales: "За окно ни одного заказа FBS: скорость 0, глубина не определена.",
  no_in_box: "Просили округлять до короба, но кратность в спеке 1С не задана — не округляли.",
  source_limited:
    "Рекомендацию срезал свободный остаток ГП на складе-источнике (он общий с FBO).",
  not_in_meta:
    "Нет SKU WB в карточке 1С — товар не продаётся через этот кабинет WB / реквизит не заполнен.",
};

/** Флаги строки словами — для подсказки у иконки. */
const flagWords = (flags: readonly string[]): string =>
  flags.map((f) => FLAG_LABEL[f] ?? f).join("; ");

/** Число из `trace` (значения там разнородные) — иначе null, и на экране «—». */
function tnum(trace: api.FbsTrace, key: string): number | null {
  const v = trace[key];
  return typeof v === "number" ? v : null;
}

/** Что таблице нужно от страницы, чтобы ячейки были живыми (правки меняются на каждый символ). */
interface TableCtx {
  edits: FbsEdits;
  errorNom: string | null;
  params: api.FbsParams;
  card: FbsCardCtx;
  onQty: (nomKey: string, raw: string) => void;
  onClear: (nomKey: string) => void;
  /** Правки, по которым сортируем и фильтруем (см. шапку файла). */
  applied: FbsEdits;
  onQtyFocus: () => void;
  onQtyBlur: () => void;
}

export function FbsTable({
  rows,
  params,
  card,
  edits,
  editError,
  onQty,
  onClear,
}: {
  rows: api.FbsReportRow[];
  /** Параметры, КОТОРЫМИ считали отчёт: из них берутся пороги подсветки. */
  params: api.FbsParams;
  /** Что карточкам «Остаток ФБС» / «В пути» нужно от отчёта (склад 1С, время, давность). */
  card: FbsCardCtx;
  edits: FbsEdits;
  editError: { nom: string; text: string } | null;
  onQty: (nomKey: string, raw: string) => void;
  onClear: (nomKey: string) => void;
}): React.ReactElement {
  // правки на момент входа в поле «К перемещению»; null — курсор не в поле, применены текущие
  const [frozen, setFrozen] = useState<FbsEdits | null>(null);
  const applied = frozen ?? edits;
  const ctxValue = (): TableCtx => ({
    edits,
    errorNom: editError?.nom ?? null,
    params,
    card,
    onQty,
    onClear,
    applied,
    onQtyFocus: () => setFrozen(ctxRef.current.edits),
    onQtyBlur: () => setFrozen(null),
  });
  const ctxRef = useRef<TableCtx>(ctxValue());
  ctxRef.current = ctxValue();

  const columns = useMemo<ColumnDef<api.FbsReportRow>[]>(() => {
    // фильтруем САМИ тем же правилом, что и вся секция (`passesFilter`); собственный `filterFn`
    // обязателен — по числу библиотека иначе подставляет `inNumberRange` и снимает наш фильтр
    const numFilter: FilterFn<api.FbsReportRow> = (row, id, value) =>
      passesFilter("number", row.getValue(id), value);
    const textFilter: FilterFn<api.FbsReportRow> = (row, id, value) =>
      passesFilter("text", row.getValue(id), value);
    const listFilter: FilterFn<api.FbsReportRow> = (row, id, value) =>
      passesFilter("list", row.getValue(id), value);
    const num = (
      id: string,
      title: string,
      value: (r: api.FbsReportRow) => number | undefined,
      cell?: ColumnDef<api.FbsReportRow>["cell"],
    ): ColumnDef<api.FbsReportRow> => ({
      id,
      accessorFn: value,
      filterFn: numFilter,
      sortUndefined: "last",
      meta: { title, filter: "number" } satisfies ColMeta,
      cell: cell ?? (({ getValue }) => int0(getValue<number>())),
    });
    return [
      {
        id: "article",
        accessorFn: (r) => r.article,
        filterFn: listFilter,
        meta: { title: "Артикул", filter: "list", left: true } satisfies ColMeta,
        cell: ({ row }) =>
          row.original.article || <span className="text-muted-foreground">—</span>,
      },
      {
        id: "name",
        accessorFn: (r) => r.nom_name,
        filterFn: textFilter,
        meta: { title: "Товар", filter: "text", left: true } satisfies ColMeta,
        cell: ({ row }) =>
          row.original.nom_name || <span className="text-muted-foreground">без имени</span>,
      },
      num("orders", "Продаж", (r) => r.orders_qty),
      num("speed", "Скор., шт/дн", (r) => r.speed, ({ getValue }) => dec1(getValue<number>())),
      // число с карточкой разбивки (`FbsInfoCards`); минус и «пусто при продажах» — красным
      num("stock", "Остаток ФБС", (r) => r.stock_qty, ({ row }) => (
        <StockCell
          row={row.original}
          ctx={ctxRef.current.card}
          valueClassName={stockClass(row.original)}
        />
      )),
      num("transit", "В пути", (r) => r.in_transit_qty, ({ row }) => (
        <TransitCell row={row.original} ctx={ctxRef.current.card} />
      )),
      num("depth", "Глубина, дн", (r) => r.depth_days ?? undefined, ({ row }) =>
        row.original.depth_days === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className={depthClass(row.original, ctxRef.current.params)}>
            {dec1(row.original.depth_days)}
          </span>
        ),
      ),
      num("need", "Потребность", (r) => r.need, ({ getValue }) => dec1(getValue<number>())),
      num("source_free", "Остаток ГП", (r) => r.source_free),
      // заводской короб (кратность из спеки 1С); 0 — не задан: «—», сортировка ставит их в конец
      num("in_box", "Короб", (r) => (r.in_box > 0 ? r.in_box : undefined), ({ row }) =>
        row.original.in_box > 0 ? (
          int0(row.original.in_box)
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      ),
      // сортируем/фильтруем по ПРИМЕНЁННЫМ правкам, показываем живое — см. шапку файла
      num("qty", "К перемещению", (r) => initialQty(r, ctxRef.current.applied), ({ row }) => (
        <QtyCell row={row.original} ctx={ctxRef.current} />
      )),
      {
        id: "marks",
        enableSorting: false,
        meta: { title: "Флаги", left: true } satisfies ColMeta,
        cell: ({ row }) => <MarksCell row={row.original} />,
      },
    ];
  }, []);

  const [sorting, setSorting] = useState([{ id: "need", desc: true }]);
  // новая ссылка на массив при смене применённых правок — иначе TanStack не пересчитает сортировку и
  // фильтр «К перемещению» (значение колонки читает правки, которых нет в данных строк); строки и их
  // id те же — фокус в поле не теряется
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `applied` и есть причина пересчёта
  const data = useMemo(() => rows.slice(), [rows, applied]);
  const table = useReactTable({
    data,
    columns,
    getRowId: (r) => r.nom_key,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSortingRemoval: false, // «без сортировки» здесь нечем заменить: порядок нужен всегда
  });
  const visible = table.getRowModel().rows;
  const visibleCols = table.getVisibleLeafColumns();
  const visibleIds = useMemo(() => visibleCols.map((c) => c.id), [visibleCols]);
  const { gridRef, gridTemplate, vars, startResize } = useColumnResize(
    FRS_STORAGE,
    DEFAULT_FRS,
    visibleIds,
  );

  // ИТОГО — по ВИДИМЫМ (отфильтрованным) строкам, поэтому строка всегда согласована с таблицей
  const totals = useMemo(() => {
    let orders = 0;
    let stock = 0;
    let transit = 0;
    let sourceFree = 0;
    let qty = 0;
    for (const row of visible) {
      const r = row.original;
      orders += r.orders_qty;
      stock += r.stock_qty;
      transit += r.in_transit_qty;
      sourceFree += r.source_free;
      qty += initialQty(r, edits);
    }
    return { orders, stock, transit, sourceFree, qty };
  }, [visible, edits]);
  const totalCells: Record<string, React.ReactNode> = {
    article: "ИТОГО",
    name: `${visible.length} тов.`,
    orders: int0(totals.orders),
    stock: int0(totals.stock),
    transit: int0(totals.transit),
    source_free: int0(totals.sourceFree),
    qty: int0(totals.qty),
  };

  return (
    <div className="space-y-2">
      <Legend horizonDays={params.horizon_days} />

      {editError !== null && (
        <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
          «К перемещению»: {editError.text} — строка не принята, осталось прежнее число.
        </p>
      )}

      <FilterChips table={table} />

      {/* Фильтр сужает ПОКАЗ, а документ собирается по всем строкам расчёта (так работает Э4) —
          сказано словами, иначе «ИТОГО 3 шт» и «к перевозу 192 шт» над таблицей читаются как
          расхождение в числах. */}
      {visible.length < rows.length && (
        <p className="px-1 text-xs text-muted-foreground">
          Фильтр показывает {visible.length} из {rows.length} строк: ИТОГО считается по видимым, а
          в заказ на перемещение уйдут ВСЕ строки с количеством (их число — в сводке над таблицей).
        </p>
      )}

      <div ref={gridRef} style={vars} className="rounded-md border">
        {table.getHeaderGroups().map((hg) => (
          <div
            key={hg.id}
            className="grid border-b bg-muted/50 text-xs font-medium text-muted-foreground"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            {hg.headers.map((h, i) => (
              <HeaderCell
                key={h.id}
                column={h.column}
                last={i === hg.headers.length - 1}
                onResize={(e) => startResize(h.column.id, e)}
              />
            ))}
          </div>
        ))}

        <div
          className="grid border-b bg-muted/60 text-xs font-semibold"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {visibleCols.map((c) => (
            <Cell key={c.id} text={metaOf(c).left}>
              {totalCells[c.id] ?? null}
            </Cell>
          ))}
        </div>

        <div className="max-h-[68vh] overflow-auto">
          {visible.map((row) => (
            <div
              key={row.id}
              className="grid w-full items-center border-b text-xs transition-colors last:border-0 hover:bg-accent/60"
              style={{ gridTemplateColumns: gridTemplate, minHeight: ROW_H }}
            >
              {row.getVisibleCells().map((cell) => (
                <Cell
                  key={cell.id}
                  text={metaOf(cell.column).left}
                  title={cell.column.id === "name" ? row.original.nom_name : undefined}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Cell>
              ))}
            </div>
          ))}
          {visible.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Ничего не найдено по фильтрам.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Легенда подсветки — теми же классами, что и ячейки (`fbsHighlight.ts`). */
function Legend({ horizonDays }: { horizonDays: number }): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground">
      <span className="text-muted-foreground/60">Подсветка:</span>
      <span>
        <span className={DANGER_CLASS}>остаток ФБС</span> — пусто, а продажи идут; минус —
        несобранных заказов больше, чем лежит
      </span>
      <span>
        <span className={DANGER_CLASS}>глубина</span> — не дотянет до завоза (&lt; {horizonDays} дн)
      </span>
      <span>
        <span className={CUT_CLASS}>к перемещению</span> — срезано остатком ГП, везти нечего
      </span>
      <span>
        <AlertTriangle className="inline size-3.5 text-amber-500" /> — флаги строки, наведите
      </span>
    </div>
  );
}

/** «К перемещению»: живое число с правкой и возврат к расчётному. */
function QtyCell({ row, ctx }: { row: api.FbsReportRow; ctx: TableCtx }): React.ReactElement {
  const edited = ctx.edits[row.nom_key];
  const value = initialQty(row, ctx.edits);
  return (
    <div className="flex items-center gap-1">
      <input
        value={String(value)}
        onChange={(e) => ctx.onQty(row.nom_key, e.target.value)}
        onFocus={ctx.onQtyFocus}
        onBlur={ctx.onQtyBlur}
        aria-label={`К перемещению ${row.article || row.nom_name}`}
        title={`Расчётное значение: ${int0(row.recommended)} шт`}
        className={cn(
          "h-6 w-full min-w-0 rounded border bg-background px-1 text-right text-xs tabular-nums focus:border-primary focus:outline-none",
          qtyClass(row),
          ctx.errorNom === row.nom_key ? "border-destructive" : "border-input",
        )}
      />
      {/* строку тронули, и число разошлось с расчётом — есть что возвращать */}
      {edited !== undefined && edited !== row.recommended && (
        <button
          type="button"
          title={`Вернуть расчётное значение (${int0(row.recommended)})`}
          onClick={() => ctx.onClear(row.nom_key)}
          className="shrink-0 rounded px-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          ↺
        </button>
      )}
    </div>
  );
}

/** Узкая колонка со значками: флаги строки (подсказкой) и «Почему?». */
function MarksCell({ row }: { row: api.FbsReportRow }): React.ReactElement {
  return (
    <span className="flex items-center gap-1">
      {row.flags.length > 0 && (
        <span title={flagWords(row.flags)} className="flex shrink-0 items-center">
          <AlertTriangle className="size-3.5 text-amber-500" />
        </span>
      )}
      <FbsWhyPopover row={row} />
    </span>
  );
}

/** «Почему?» по строке: перечисление слагаемых расчёта из `trace`. */
function FbsWhyPopover({ row }: { row: api.FbsReportRow }): React.ReactElement {
  const t = row.trace;
  const horizon = tnum(t, "horizon_days");
  const window = tnum(t, "window_days");
  const speed = tnum(t, "speed");
  const raw = tnum(t, "recommended_raw");
  const boxed = tnum(t, "recommended_boxed");
  const inBox = tnum(t, "in_box");
  const num = (v: number | null): string => (v === null ? "—" : fmt(v));
  // вычитаемое со знаком минус — в скобках, иначе «− -5» читается как опечатка
  const sub = (v: number | null): string => (v !== null && v < 0 ? `(${fmt(v)})` : num(v));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Почему столько?"
          className="shrink-0 rounded text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 text-xs">
        <div className="space-y-2">
          <div className="border-b pb-1.5 text-sm font-semibold">
            Почему {int0(row.recommended)} шт?
          </div>
          <div className="space-y-1">
            <WhyRow k={`Заказов FBS за ${num(window)} дн`} v={num(tnum(t, "orders_qty"))} />
            <WhyRow k="Скорость, шт/дн" v={num(speed)} />
            <WhyRow k="Едет (свежие перемещения)" v={num(tnum(t, "in_transit_qty"))} />
          </div>
          <StockWhy row={row} />
          <div className="rounded bg-muted/40 px-2 py-1 font-mono text-[11px] text-muted-foreground">
            {num(horizon)} дн × {num(speed)} − {sub(tnum(t, "stock_qty"))} −{" "}
            {num(tnum(t, "in_transit_qty"))} = {num(tnum(t, "need"))} шт потребности
          </div>
          <div className="space-y-1 border-t pt-1.5">
            <WhyRow k="Вверх до целого" v={num(raw)} />
            {boxed !== null && raw !== null && boxed !== raw && (
              <WhyRow k={`Вверх до короба (${num(inBox)} шт)`} v={num(boxed)} />
            )}
            <WhyRow k="Свободно ГП на источнике" v={num(tnum(t, "source_free"))} />
            <WhyRow k="К перемещению по расчёту" v={num(tnum(t, "recommended"))} strong />
          </div>
          {row.flags.length > 0 && (
            <div className="space-y-1 border-t pt-1.5">
              {row.flags.map((f) => (
                <div key={f} className="text-muted-foreground">
                  <b className="text-foreground">{FLAG_LABEL[f] ?? f}</b> — {FLAG_HINT[f] ?? ""}
                </div>
              ))}
            </div>
          )}
          {row.mp_skus.length > 0 && (
            <div className="border-t pt-1.5 font-mono text-[11px] text-muted-foreground">
              {row.mp_skus.join(", ")}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Из чего сложился «Остаток ФБС»: юрлица комнаты минус уже обещанное (`fbsStock.ts`). */
function StockWhy({ row }: { row: api.FbsReportRow }): React.ReactElement {
  const d = row.stock_detail;
  const b = stockBreakdown(d, row.stock_qty);
  return (
    <div className="space-y-1 border-t pt-1.5" data-testid="fbs-why-stock">
      <div className="text-muted-foreground">
        Остаток ФБС —{" "}
        {d.source === "wh_helper" ? "пул комнаты wh-helper (как в WB)" : "свободный 1С склада"}
      </div>
      {b.terms.map((t) => (
        <WhyRow
          key={t.key}
          k={`${t.sign === "−" ? "− " : ""}${t.label}`}
          v={fmt(t.qty)}
          title={t.title}
        />
      ))}
      <WhyRow
        k="Остаток ФБС"
        v={fmt(row.stock_qty)}
        strong
        className={row.stock_qty < 0 ? DANGER_CLASS : undefined}
        title={row.stock_qty < 0 ? NEGATIVE_STOCK_HINT : undefined}
      />
      {b.mismatch && (
        <div className="text-destructive">
          Разбивка ({fmt(b.sum)}) не сходится с остатком ({fmt(row.stock_qty)}) — ошибка расчёта,
          сообщите разработчику.
        </div>
      )}
    </div>
  );
}

function WhyRow({
  k,
  v,
  strong,
  className,
  title,
}: {
  k: string;
  v: string;
  strong?: boolean;
  className?: string;
  title?: string;
}): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3" title={title}>
      <span className="truncate text-muted-foreground">{k}</span>
      <span className={cn("tabular-nums", strong && "font-semibold", className)}>{v}</span>
    </div>
  );
}
